import * as Sentry from '@sentry/react';
import { Component, type ErrorInfo, type ReactNode } from 'react';

import { reportarErro } from '@/api/reportar-erro';
import { EstadoErro } from '@/shared/components/estados';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  erro: Error | null;
}

/**
 * Sem isto, um throw em qualquer componente desmonta a árvore inteira e o
 * usuário fica com a tela branca — sem mensagem e sem nada para reportar.
 */
export class ErrorBoundary extends Component<Props, State> {
  override state: State = { erro: null };

  static getDerivedStateFromError(erro: Error): State {
    return { erro };
  }

  override componentDidCatch(erro: Error, info: ErrorInfo): void {
    Sentry.captureException(erro, { extra: { componentStack: info.componentStack } });
    // Também para Administração → Alertas: o Sentry fica com a equipe técnica,
    // os alertas com quem administra a plataforma.
    reportarErro({
      tipo: 'render',
      mensagem: erro.message,
      stack: [erro.stack, info.componentStack].filter(Boolean).join('\n'),
    });
  }

  override render(): ReactNode {
    if (!this.state.erro) return this.props.children;
    if (this.props.fallback) return this.props.fallback;

    return (
      <EstadoErro
        titulo="Erro inesperado"
        descricao="A página encontrou um erro. Recarregar costuma resolver."
        aoTentarDeNovo={() => window.location.reload()}
      />
    );
  }
}
